'use client';

import React, { useMemo } from 'react';
import { useInstantSearch } from 'react-instantsearch';

import {
  getHeaderHeight,
  SCROLL_RESET_VISUAL_GAP_PX,
} from '@/utils/cssVariables';

/**
 * Composant qui scrolle automatiquement vers le haut du conteneur des résultats lors des changements
 *
 * Principe SOLID: Single Responsibility
 * - Responsable uniquement du scroll automatique lors des changements de filtres/page
 *
 * Principe Clean Code:
 * - Composant simple et focalisé
 * - Utilise useEffect pour détecter les changements
 * - Constantes nommées pour les valeurs magiques
 *
 * @description
 * Surveille les changements dans indexUiState pour détecter :
 * - Changement de page (desktop + mobile)
 * - Changement de filtres/refinements (desktop + mobile)
 * - Changement de tri (mobile uniquement - car en desktop la barre de contrôles reste visible)
 * Ne scrolle PAS lors des changements de query (searchbox)
 *
 * Performance:
 * - Utilise useMemo pour éviter la re-sérialisation inutile
 * - Période de grâce de 5000ms au mount pour éviter le scroll au refresh
 * - Détection responsive (desktop/mobile) pour comportement adaptatif
 */
export function ScrollToResultsOnChange() {
  const { indexUiState } = useInstantSearch();
  const previousPageRef = React.useRef<number | undefined>(undefined);
  const previousRefinementsRef = React.useRef<string | undefined>(undefined);
  const previousSortByRef = React.useRef<string | undefined>(undefined);
  // `false` until the initial load has settled. InstantSearch hydrates its UI
  // state from the URL across several renders right after mount (page=undefined
  // → page=3, refinements parsed, etc.). Those settling renders must NOT scroll,
  // otherwise deep-linking to /recherche?page=3 yanks the viewport on load.
  // We arm scrolling only after the first paint: every render up to that point
  // re-seeds the baseline refs silently, so they hold the hydrated values by the
  // time a genuine, user-driven change arrives. This replaces a 5s time window
  // that was long enough to also swallow real page changes made right after
  // landing on the page (the bug this component is meant to fix).
  const isArmedRef = React.useRef(false);
  const armFrameRef = React.useRef(0);

  // Extrait les valeurs suivies séparément
  const currentPage = indexUiState.page;
  const currentSortBy = indexUiState.sortBy;

  // Sérialise tous les refinements (optimisé avec useMemo)
  const currentRefinements = React.useMemo(() => {
    const refinements = Object.keys(indexUiState).reduce(
      (acc, key) => {
        if (key !== 'query' && key !== 'page' && key !== 'sortBy') {
          acc[key] = indexUiState[key as keyof typeof indexUiState];
        }
        return acc;
      },
      {} as Record<string, unknown>,
    );
    return JSON.stringify(refinements);
  }, [indexUiState]);

  const isMobile = useMemo(() => {
    return typeof window !== 'undefined' ? window.innerWidth < 1024 : false;
  }, []);

  // Arm scrolling after the first paint. InstantSearch's URL-hydration renders
  // all run before the browser paints, so by the time this fires the baseline
  // refs already hold the settled state and only later user-driven changes
  // scroll. Two rAFs guarantee we're past the initial commit + paint.
  React.useEffect(() => {
    const outer = requestAnimationFrame(() => {
      const inner = requestAnimationFrame(() => {
        isArmedRef.current = true;
      });
      armFrameRef.current = inner;
    });
    armFrameRef.current = outer;
    return () => cancelAnimationFrame(armFrameRef.current);
  }, []);

  React.useEffect(() => {
    // Tant que le scroll n'est pas armé (chargement initial / hydratation
    // depuis l'URL), on se contente d'enregistrer l'état courant sans scroller.
    // Cela évite un scroll intempestif quand on arrive directement sur une page
    // paginée (ex. ?page=3), sans la fenêtre temporelle de 5s qui avalait aussi
    // les vrais changements de page faits juste après l'arrivée sur la page.
    if (!isArmedRef.current) {
      previousPageRef.current = currentPage;
      previousRefinementsRef.current = currentRefinements;
      previousSortByRef.current = currentSortBy;
      return;
    }

    // Détecte les changements de page et filtres
    const pageChanged = previousPageRef.current !== currentPage;
    const refinementsChanged =
      previousRefinementsRef.current !== currentRefinements;
    const sortByChanged = previousSortByRef.current !== currentSortBy;

    // Détecte si on est sur mobile (< 1024px pour Tailwind lg breakpoint)

    // Sur desktop, ignore les changements de tri car la barre de contrôles est visible
    // Sur mobile, scroll sur changement de tri car le tri est dans une modale
    const shouldScrollOnSort = isMobile && sortByChanged;

    if (pageChanged || refinementsChanged || shouldScrollOnSort) {
      // Scroll vers le conteneur des résultats en tenant compte de la hauteur du header
      const resultsContainer = document.getElementById('search-results-main');
      if (resultsContainer) {
        const headerHeight = getHeaderHeight();

        // Calcule la position de l'élément par rapport au haut du document
        const elementRect = resultsContainer.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition =
          elementRect.top +
          scrollTop -
          headerHeight -
          SCROLL_RESET_VISUAL_GAP_PX;

        window.scrollTo({
          top: targetPosition,
          behavior: 'instant',
        });
      }

      previousPageRef.current = currentPage;
      previousRefinementsRef.current = currentRefinements;
      previousSortByRef.current = currentSortBy;
    }
  }, [currentPage, currentRefinements, currentSortBy, isMobile]);

  return null;
}
